import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ClasseComposant, ClassesComposantsService } from '../../../services/classes-composants.service';
import { Composant, ComposantPayload, ComposantsService } from '../../../services/composants.service';

@Component({
  selector: 'app-creer-composant',
  imports: [CommonModule, FormsModule],
  templateUrl: './creer-composant.component.html',
  styleUrl: './creer-composant.component.scss'
})
export class CreerComposantComponent implements OnInit, OnDestroy {
  protected composants: Composant[] = [];
  protected repriseComposantId = '';
  protected classesComposants: ClasseComposant[] = [];
  protected selectedClasseId = '';

  protected nom = '';
  protected lien = '';
  protected prix = '';
  protected standardEmballage = '';
  protected unite = '';
  protected refFabricant = '';
  protected commentaire = '';
  protected imageFile: File | null = null;
  protected loading = false;
  protected loadingComposants = false;
  protected success = '';
  protected error = '';
  protected toastMessage = '';
  protected toastType: 'success' | 'error' | '' = '';

  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly composantsService: ComposantsService,
    private readonly classesComposantsService: ClassesComposantsService,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadComposants();
    this.loadClassesComposants();
  }

  protected onReprendreComposantChange(): void {
    const selected = this.composants.find((composant) => composant.id === Number(this.repriseComposantId));
    if (!selected) {
      return;
    }

    this.nom = selected.nom ?? '';
    this.lien = selected.lien ?? '';
    this.prix = selected.prix != null ? String(selected.prix) : '';
    this.standardEmballage = selected.standard_emballage ?? '';
    this.unite = selected.unite ?? '';
    this.refFabricant = selected.ref_fabricant ?? '';
    this.commentaire = selected.commentaire ?? '';
    this.selectedClasseId = selected.classe_id != null ? String(selected.classe_id) : '';
    this.imageFile = null;
  }

  protected onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.imageFile = input.files?.[0] ?? null;
  }

  protected async submit(): Promise<void> {
    this.error = '';
    this.success = '';

    if (!this.nom.trim()) {
      this.error = 'Le nom est requis.';
      this.showToast(this.error, 'error');
      return;
    }

    if (this.prix && !this.isValidPrice(this.prix)) {
      this.error = 'Le prix doit être un nombre avec au maximum 2 décimales.';
      this.showToast(this.error, 'error');
      return;
    }

    if (this.unite && this.unite.trim().length > 3) {
      this.error = "L'unité ne doit pas dépasser 3 caractères.";
      this.showToast(this.error, 'error');
      return;
    }

    const classeId = Number(this.selectedClasseId);
    if (!classeId) {
      this.error = 'La classe du composant est obligatoire.';
      this.showToast(this.error, 'error');
      return;
    }

    const payload: ComposantPayload = {
      nom: this.nom.trim(),
      lien: this.lien,
      prix: this.prix != null && this.prix !== '' ? Number(this.prix) : null,
      standard_emballage: this.standardEmballage,
      unite: this.unite,
      ref_fabricant: this.refFabricant,
      commentaire: this.commentaire,
      classe_id: String(classeId),
    };

    if (this.imageFile) {
      try {
        payload.photo = await this.resizeImageToBase64(this.imageFile, 600);
      } catch {
        this.error = "Impossible de traiter l'image sélectionnée.";
        this.showToast(this.error, 'error');
        return;
      }
    }

    this.loading = true;
    this.composantsService.create(payload).subscribe({
      next: (created: Composant) => {
        this.zone.run(() => {
          this.loading = false;
          this.success = `Composant créé avec succès : ${created.nom}`;
          this.showToast(this.success, 'success');
          this.composants = [created, ...this.composants.filter((composant) => composant.id !== created.id)];
          this.nom = '';
          this.lien = '';
          this.prix = '';
          this.standardEmballage = '';
          this.unite = '';
          this.refFabricant = '';
          this.commentaire = '';
          this.selectedClasseId = '';
          this.imageFile = null;
          this.repriseComposantId = '';
          this.loadComposants();
          this.cdr.detectChanges();
        });
      },
      error: (err: unknown) => {
        this.zone.run(() => {
          this.loading = false;
          this.error = this.extractErrorMessage(err, 'Création impossible.');
          this.showToast(this.error, 'error');
          this.cdr.detectChanges();
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
  }

  private isValidPrice(value: unknown): boolean {
    const normalized = String(value ?? '').trim();
    return /^\d+(\.\d{1,2})?$/.test(normalized);
  }

  private isValidPositiveInteger(value: unknown): boolean {
    const normalized = String(value ?? '').trim();
    return /^[1-9]\d*$/.test(normalized);
  }

  private resizeImageToBase64(file: File, maxDimension: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onerror = () => reject(new Error('read_error'));
      reader.onload = async () => {
        const src = String(reader.result ?? '');
        if (!src) {
          reject(new Error('empty_source'));
          return;
        }

        try {
          const image = await this.loadImageFromDataUrl(src);
          const sourceWidth = image.width;
          const sourceHeight = image.height;
          if (!sourceWidth || !sourceHeight) {
            reject(new Error('invalid_dimensions'));
            return;
          }

          const inputType = (file.type || '').toLowerCase();
          const orientation =
            inputType === 'image/jpeg' || inputType === 'image/jpg'
              ? this.getJpegExifOrientation(await this.readFileAsArrayBuffer(file))
              : 1;

          const swapsAxes = orientation >= 5 && orientation <= 8;
          const orientedWidth = swapsAxes ? sourceHeight : sourceWidth;
          const orientedHeight = swapsAxes ? sourceWidth : sourceHeight;
          const scale = Math.min(1, maxDimension / Math.max(orientedWidth, orientedHeight));

          const drawWidth = Math.max(1, Math.round(sourceWidth * scale));
          const drawHeight = Math.max(1, Math.round(sourceHeight * scale));

          const canvas = document.createElement('canvas');
          canvas.width = swapsAxes ? drawHeight : drawWidth;
          canvas.height = swapsAxes ? drawWidth : drawHeight;

          const context = canvas.getContext('2d');
          if (!context) {
            reject(new Error('canvas_error'));
            return;
          }

          this.applyExifTransform(context, orientation, drawWidth, drawHeight);
          context.drawImage(image, 0, 0, drawWidth, drawHeight);

          const outputType =
            inputType === 'image/png' || inputType === 'image/webp' || inputType === 'image/jpeg'
              ? inputType
              : 'image/png';

          resolve(canvas.toDataURL(outputType, 0.9));
        } catch {
          reject(new Error('image_error'));
        }
      };

      reader.readAsDataURL(file);
    });
  }

  private loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onerror = () => reject(new Error('image_error'));
      image.onload = () => resolve(image);
      image.src = dataUrl;
    });
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error ?? new Error('read_error'));
      reader.readAsArrayBuffer(file);
    });
  }

  private applyExifTransform(
    context: CanvasRenderingContext2D,
    orientation: number,
    width: number,
    height: number
  ): void {
    switch (orientation) {
      case 2:
        context.transform(-1, 0, 0, 1, width, 0);
        break;
      case 3:
        context.transform(-1, 0, 0, -1, width, height);
        break;
      case 4:
        context.transform(1, 0, 0, -1, 0, height);
        break;
      case 5:
        context.transform(0, 1, 1, 0, 0, 0);
        break;
      case 6:
        context.transform(0, 1, -1, 0, height, 0);
        break;
      case 7:
        context.transform(0, -1, -1, 0, height, width);
        break;
      case 8:
        context.transform(0, -1, 1, 0, 0, width);
        break;
      default:
        break;
    }
  }

  private getJpegExifOrientation(buffer: ArrayBuffer): number {
    const view = new DataView(buffer);
    if (view.byteLength < 4 || view.getUint16(0, false) !== 0xFFD8) {
      return 1;
    }

    let offset = 2;
    while (offset + 4 <= view.byteLength) {
      const marker = view.getUint16(offset, false);
      offset += 2;

      if (marker === 0xFFE1) {
        if (offset + 2 > view.byteLength) {
          return 1;
        }

        const segmentLength = view.getUint16(offset, false);
        const segmentStart = offset + 2;
        if (segmentStart + segmentLength - 2 > view.byteLength) {
          return 1;
        }

        if (view.getUint32(segmentStart, false) !== 0x45786966) {
          return 1;
        }

        const tiffOffset = segmentStart + 6;
        if (tiffOffset + 8 > view.byteLength) {
          return 1;
        }

        const littleEndian = view.getUint16(tiffOffset, false) === 0x4949;
        const firstIfdOffset = view.getUint32(tiffOffset + 4, littleEndian);
        const ifdStart = tiffOffset + firstIfdOffset;
        if (ifdStart + 2 > view.byteLength) {
          return 1;
        }

        const entries = view.getUint16(ifdStart, littleEndian);
        for (let i = 0; i < entries; i += 1) {
          const entryOffset = ifdStart + 2 + (i * 12);
          if (entryOffset + 12 > view.byteLength) {
            return 1;
          }

          const tag = view.getUint16(entryOffset, littleEndian);
          if (tag === 0x0112) {
            const orientation = view.getUint16(entryOffset + 8, littleEndian);
            return orientation >= 1 && orientation <= 8 ? orientation : 1;
          }
        }

        return 1;
      }

      if ((marker & 0xFF00) !== 0xFF00 || marker === 0xFFDA || marker === 0xFFD9) {
        break;
      }

      if (offset + 2 > view.byteLength) {
        break;
      }

      const segmentLength = view.getUint16(offset, false);
      offset += segmentLength;
    }

    return 1;
  }

  private extractErrorMessage(err: unknown, fallback: string): string {
    const maybe = err as { error?: { detail?: unknown }; message?: string };
    const detail = maybe?.error?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string; message?: string };
      return first?.msg ?? first?.message ?? fallback;
    }
    if (typeof maybe?.message === 'string' && maybe.message.trim()) {
      return maybe.message;
    }
    return fallback;
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = setTimeout(() => {
      this.zone.run(() => {
        this.toastMessage = '';
        this.toastType = '';
        this.toastTimer = null;
        this.cdr.detectChanges();
      });
    }, 5000);
  }

  private loadClassesComposants(): void {
    this.classesComposantsService.list().subscribe({
      next: (items: ClasseComposant[]) => {
        this.classesComposants = items;
        this.cdr.detectChanges();
      },
      error: () => {
        this.error = 'Impossible de charger les classes composants.';
        this.cdr.detectChanges();
      }
    });
  }

  private loadComposants(): void {
    this.loadingComposants = true;
    this.composantsService.list().subscribe({
      next: (items: Composant[]) => {
        this.loadingComposants = false;
        this.composants = Array.isArray(items) ? items : [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingComposants = false;
        this.composants = [];
        this.cdr.detectChanges();
      }
    });
  }
}
